import { Routes } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';
import { LoginGuard } from './core/guards/login.guard';
import { PermissionGuard } from './core/guards/permission.guard';
import { MenuType } from './core/constants/permissions';

export const routes: Routes = [
  { path: '', redirectTo: 'dashboard', pathMatch: 'full' },

  // Auth routes (guest only, no admin layout)
  {
    path: 'auth',
    canActivate: [LoginGuard],
    loadComponent: () =>
      import('./layouts/auth-layout/auth-layout.component').then(m => m.AuthLayoutComponent),
    children: [
      { path: '', redirectTo: 'login', pathMatch: 'full' },
      {
        path: 'login',
        loadComponent: () => import('./features/authentication/login/login.component').then(m => m.LoginComponent),
        title: 'Sign In — MediAudit',
      },
      {
        path: 'forgot-password',
        loadComponent: () => import('./features/authentication/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent),
        title: 'Forgot Password — MediAudit',
      },
      {
        path: 'reset-password',
        loadComponent: () => import('./features/authentication/reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
        title: 'Reset Password — MediAudit',
      },
      {
        path: 'accept-invitation',
        loadComponent: () => import('./features/authentication/accept-invitation/accept-invitation.component').then(m => m.AcceptInvitationComponent),
        title: 'Accept Invitation — MediAudit',
      },
      {
        path: 'otp-verification',
        loadComponent: () => import('./features/authentication/otp-verification/otp-verification.component').then(m => m.OtpVerificationComponent),
        title: 'OTP Verification — MediAudit',
      },
    ],
  },

  // Admin routes (protected, inside admin layout with sidebar + header)
  {
    path: '',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./layouts/admin-layout/admin-layout.component').then(m => m.AdminLayoutComponent),
    children: [
      {
        path: 'dashboard',
        canActivate: [PermissionGuard],
        loadComponent: () => import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent),
        title: 'Dashboard — MediAudit',
        data: { title: 'Dashboard', subtitle: '', menuType: MenuType.Dashboard },
      },
      {
        path: 'users',
        canActivate: [PermissionGuard],
        children: [
          {
            path: '',
            loadComponent: () => import('./features/users/users-list/users-list.component').then(m => m.UsersListComponent),
            title: 'Users & Access — MediAudit',
            data: { title: 'Users & Access', subtitle: '' },
          },
          {
            path: 'user-form',
            loadComponent: () => import('./features/users/users-list/user-form/user-form.component').then(m => m.UserFormComponent),
            title: 'Add User — MediAudit',
            data: { title: 'Add User', subtitle: '' },
          },
          {
            path: 'user-form/:id',
            loadComponent: () => import('./features/users/users-list/user-form/user-form.component').then(m => m.UserFormComponent),
            title: 'Edit User — MediAudit',
            data: { title: 'Edit User', subtitle: '' },
          },
          {
            path: 'role-form',
            loadComponent: () => import('./features/users/roles-list/role-form/role-form.component').then(m => m.RoleFormComponent),
            title: 'Add Role — MediAudit',
            data: { title: 'Add Role', subtitle: '' },
          },
          {
            path: 'role-form/:id',
            loadComponent: () => import('./features/users/roles-list/role-form/role-form.component').then(m => m.RoleFormComponent),
            title: 'Edit Role — MediAudit',
            data: { title: 'Edit Role', subtitle: '' },
          },
        ],
        data: { title: 'Users & Access', subtitle: '', menuType: MenuType.User },
      },
      {
        path: 'organizations',
        canActivate: [PermissionGuard],
        children: [
          {
            path: '',
            loadComponent: () => import('./features/organizations/organizations.component').then(m => m.OrganizationsComponent),
            title: 'Organizations — MediAudit',
            data: { title: 'Organizations', subtitle: '' },
          },
          // {
          //   path: 'create',
          //   loadComponent: () => import('./features/organizations/create-organizations/create-organizations').then(m => m.CreateOrganizations),
          //   title: 'Create Organization — MediAudit',
          //   data: { title: 'Create Organization', subtitle: '' },
          // },
          {
            path: 'view/:id',
            loadComponent: () => import('./features/organizations/view-organization/view-organization.component').then(m => m.ViewOrganizationComponent),
            title: 'Organization Details — MediAudit',
            data: { title: 'Organization Details', subtitle: '' },
          }
        ],
        data: { title: 'Organizations', subtitle: '', menuType: MenuType.Organization },
      },
      {
        path: 'masters',
        canActivate: [PermissionGuard],
        children: [
          {
            path: '',
            loadComponent: () => import('./features/masters/masters.component').then(m => m.MastersComponent),
            title: 'Org Masters Data — MediAudit',
            data: { title: 'Org Masters Data', subtitle: '' },
          },
          {
            // Org id rides along with the record id — the detail endpoint is
            // org-scoped and this route is reachable directly by URL.
            path: 'bhs-matrix/view/:orgId/:id',
            loadComponent: () => import('./features/masters/bhs-matrix/view-bhs-matrix/view-bhs-matrix.component').then(m => m.ViewBhsMatrixComponent),
            title: 'BHS Matrix Details — MediAudit',
            data: { title: 'BHS Matrix Details', subtitle: '' },
          },
        ],
        data: { title: 'Org Masters Data', subtitle: '', menuType: MenuType.Masters },
      },
      {
        path: 'security-policy',
        canActivate: [PermissionGuard],
        loadComponent: () => import('./features/security-policy/security-policy.component').then(m => m.SecurityPolicyComponent),
        title: 'Security Policy — MediAudit',
        data: { title: 'Security Policy', subtitle: '', menuType: MenuType.Policy },
      },
      {
        path: 'audit-logs',
        canActivate: [PermissionGuard],
        loadComponent: () => import('./features/audit-logs/audit-logs.component').then(m => m.AuditLogsComponent),
        title: 'Audit Logs — MediAudit',
        data: { title: 'Audit Logs', subtitle: '', menuType: MenuType.Audit },
      },
      {
        path: 'profile',
        loadComponent: () => import('./features/profile/profile.component').then(m => m.ProfileComponent),
        title: 'My Profile — MediAudit',
        data: { title: 'My Profile', subtitle: '' },
      },
    ],
  },

  // 404
   {
    path: '**',
    redirectTo: 'dashboard',
  },
  // {
  //   path: '**',
  //   loadComponent: () => import('./features/not-found/not-found.component').then(m => m.NotFoundComponent),
  //   title: '404 — MediAudit',
  // },
];
